import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox, Container, Loader, Popup, SortableTable } from 'components/UIElements'
import { NOT_APPLICABLE_PLACEHOLDER } from 'utils/constants'
import STRINGS from 'utils/strings'
import StatusHistorySection from './StatusHistorySection'
import StatusTimestampTooltip from './StatusTimestampTooltip'
import { canViewInstrumentResponses } from '../utils/submissionDisplayUtils'

const InstrumentSubmissionViewer = lazy(() =>
  import(/* webpackChunkName: "instrument-submission-viewer" */ './InstrumentSubmissionViewer'),
)

const noInstrumentsString = 'There are no pending instruments for this participant.'

class CompletedInstruments extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      currentCycle: null,
      instrumentsToComplete: {},
      isStatusRowExpanded: {},
      selectedSubmissionRow: null,
      openActionsMenuId: null,
    }
  }

  closeActionsMenu = () => {
    this.setState({ openActionsMenuId: null })
  }

  usesInstrumentActionsMenu = () => {
    const { isV2Architecture, hasAdvancedEventTimelines } = this.props
    // GOLD-12407: Name + Actions menu for v2 studies and advanced-timeline studies.
    return !!(isV2Architecture || hasAdvancedEventTimelines)
  }

  showInstrumentStatusColumns = () => {
    const { hasAdvancedEventTimelines, hideInstrumentStatus } = this.props
    return hasAdvancedEventTimelines && !hideInstrumentStatus
  }

  canShowInstrumentStatusAction = () => {
    const { hasAdvancedEventTimelines, hideInstrumentStatus } = this.props
    return hasAdvancedEventTimelines && !hideInstrumentStatus
  }

  headerOptions = () => {
    const {
      hasAdvancedEventTimelines,
      hasMarkInstrumentComplete,
      site,
      mainSite,
      isV2Architecture,
    } = this.props
    const showStatusColumns = this.showInstrumentStatusColumns()

    if (this.usesInstrumentActionsMenu()) {
      return [
        { key: 'name', sortable: true, component: 'Instrument Name' },
        { key: 'action', sortable: false, component: 'Actions', className: 'instrument-actions-cell' },
      ]
    }

    const headers = showStatusColumns
      ? [
          { key: 'name', sortable: true, component: 'Instrument Name' },
          { key: 'status', sortable: true, component: 'Status' },
          {
            key: 'statusTimestamp',
            sortable: true,
            component: 'Status Timestamp',
            iconClassName: 'fas fa-info-circle',
            className: 'status-timestamp-cell',
            popupComponent: <StatusTimestampTooltip site={site} mainSite={mainSite} />,
          },
          { key: 'action', sortable: false, component: '' },
        ]
      : hasAdvancedEventTimelines
        ? [{ key: 'name', sortable: true, component: 'Instrument Name' }]
      : [
          { key: 'name', sortable: true, component: 'Instrument Name' },
          { key: 'cycleNo', sortable: true, component: 'Chapter Number' },
          {
            key: 'completionDate',
            sortable: true,
            component: 'Completion',
            iconClassName: 'fas fa-info-circle',
            popupText: "Participant's local time",
          },
          { key: 'action', sortable: false, component: isV2Architecture ? 'Action' : '' },
        ]
    if (!hasMarkInstrumentComplete && !isV2Architecture) return headers.filter(col => col.key !== `action`)
    return headers
  }

  clearInstrumentsToComplete = () => {
    this.setState({ instrumentsToComplete: {} })
  }

  renderMarkCompleteButton = () => {
    const { markInstrumentComplete, params, ptpInstrumentsLoading, ptpId: ptpID, ptpSiteID } = this.props
    const { studyID } = params
    const { instrumentsToComplete } = this.state
    const noInstrumentsToComplete = Object.keys(instrumentsToComplete).length === 0
    return (
      <Button
        className={`mark-complete flexed center-aligned${ptpInstrumentsLoading ? ' loading' : ''}`}
        content='Mark completed'
        disabled={noInstrumentsToComplete}
        loading={ptpInstrumentsLoading}
        icon='fas fa-check'
        onClick={() =>
          markInstrumentComplete(
            { instrumentsToComplete, ptpID, studyID, siteID: ptpSiteID },
            this.clearInstrumentsToComplete,
          )
        }
      />
    )
  }

  toggleInstrumentToComplete = idqId => {
    const { instrumentsToComplete } = this.state
    const newInstrumentsToComplete = { ...instrumentsToComplete }
    if (instrumentsToComplete[idqId]) {
      delete newInstrumentsToComplete[idqId]
    } else {
      newInstrumentsToComplete[idqId] = true
    }
    this.setState({ instrumentsToComplete: newInstrumentsToComplete })
  }

  getRowActionMeta = row => {
    const actionCell = row[row.length - 1]
    const {
      idqId,
      completed,
      instrumentType,
      instanceId,
      title,
      statusTimestamp,
      statusEventType,
    } = actionCell

    return {
      idqId,
      completed,
      instrumentType,
      instanceId,
      title: title || row[0]?.value,
      statusTimestamp,
      statusEventType,
    }
  }

  openSubmissionViewer = rowMeta => {
    this.setState({
      selectedSubmissionRow: {
        instanceId: rowMeta.instanceId || rowMeta.idqId,
        title: rowMeta.title,
        statusTimestamp: rowMeta.statusTimestamp,
        statusEventType: rowMeta.statusEventType,
      },
      isStatusRowExpanded: {},
      openActionsMenuId: null,
    })
  }

  getViewableInstrumentOptions = () => {
    const { isV2Architecture } = this.props

    return this.getFilteredByCycle()
      .map(row => this.getRowActionMeta(row))
      .filter(rowMeta =>
        canViewInstrumentResponses({
          completed: rowMeta.completed,
          instrumentType: rowMeta.instrumentType,
          isV2Architecture,
        }),
      )
      .map(rowMeta => ({
        key: rowMeta.instanceId || rowMeta.idqId,
        value: rowMeta.instanceId || rowMeta.idqId,
        text: rowMeta.title,
        rowMeta,
      }))
  }

  handleSelectInstrument = item => {
    if (!item?.rowMeta) return
    this.openSubmissionViewer(item.rowMeta)
  }

  openStatusTimeline = rowMeta => {
    const { idqId } = rowMeta
    this.setState({
      selectedSubmissionRow: null,
      openActionsMenuId: null,
      isStatusRowExpanded: {
        [idqId]: true,
      },
    })
  }

  handleInstrumentRowClick = row => {
    const { isV2Architecture } = this.props
    if (this.usesInstrumentActionsMenu()) return

    const { isStatusRowExpanded } = this.state
    const rowMeta = this.getRowActionMeta(row)
    const showViewResponses = canViewInstrumentResponses({
      completed: rowMeta.completed,
      instrumentType: rowMeta.instrumentType,
      isV2Architecture,
    })

    if (showViewResponses) {
      this.openSubmissionViewer(rowMeta)
      return
    }

    if (this.canShowInstrumentStatusAction() && rowMeta.idqId) {
      this.setState({
        isStatusRowExpanded: {
          [rowMeta.idqId]: !isStatusRowExpanded[rowMeta.idqId],
        },
      })
    }
  }

  closeSubmissionViewer = () => {
    const { resetInstrumentSubmission } = this.props
    resetInstrumentSubmission()
    this.setState({ selectedSubmissionRow: null })
  }

  renderInstrumentActionsCell = (cell, idx) => {
    const { hasMarkInstrumentComplete, isV2Architecture } = this.props
    const {
      idqId,
      completed,
      value,
      instrumentType,
      title,
      instanceId,
      statusTimestamp,
      statusEventType,
      row: fullRow,
    } = cell

    if (!idqId) return null

    const rowMeta = {
      idqId,
      completed,
      instrumentType,
      instanceId: instanceId || idqId,
      title: title || fullRow?.[0]?.value,
      statusTimestamp,
      statusEventType,
    }
    const showViewResponses = canViewInstrumentResponses({
      completed,
      instrumentType,
      isV2Architecture,
    })
    const showViewStatus = this.canShowInstrumentStatusAction()
    const hasMenuActions = showViewResponses || showViewStatus
    const isActionsDisabled = !hasMenuActions
    const { instrumentsToComplete, openActionsMenuId } = this.state
    const isMarkedComplete = value === 'Marked complete'
    const isMenuOpen = openActionsMenuId === idqId
    const actionsTitle = isActionsDisabled
      ? 'No instrument actions available'
      : 'Instrument actions'

    return (
      <td className='instrument-actions-cell' key={`instrument-actions-${idqId}-${idx}`}>
        <div className='instrument-actions-content'>
          {hasMarkInstrumentComplete && (
            <span className='instrument-mark-complete-action'>
              {completed ? (
                <>{isMarkedComplete ? value : ''}</>
              ) : (
                <Checkbox
                  label='Completed'
                  checked={instrumentsToComplete[idqId]}
                  onClick={event => {
                    event.stopPropagation()
                    this.toggleInstrumentToComplete(idqId)
                  }}
                />
              )}
            </span>
          )}
          <Popup
            dark
            disabled={isActionsDisabled}
            open={isMenuOpen}
            onOpen={() => this.setState({ openActionsMenuId: idqId })}
            onClose={this.closeActionsMenu}
            noPointer={false}
            position='bottom'
            className={`instrument-actions-popup${isActionsDisabled ? ' instrument-actions-popup-disabled' : ''}`}
            trigger={
              <Button
                link
                type='button'
                disabled={isActionsDisabled}
                icon='fas fa-pencil-alt'
                title={actionsTitle}
                aria-label={actionsTitle}
              />
            }>
            <>
              {showViewResponses && (
                <Button
                  className='close-trigger instrument-action-item'
                  no-margin
                  content={STRINGS.viewInstrumentResponses}
                  onClick={() => {
                      this.closeActionsMenu()
                      this.openSubmissionViewer(rowMeta)
                    }}
                />
              )}
              {showViewStatus && (
                <Button
                  className='close-trigger instrument-action-item'
                  no-margin
                  content={STRINGS.viewInstrumentStatus}
                  onClick={() => {
                      this.closeActionsMenu()
                      this.openStatusTimeline(rowMeta)
                    }}
                />
              )}
            </>
          </Popup>
        </div>
      </td>
    )
  }

  renderCompleteInstrumentCell = (cell, idx) => {
    const { hasMarkInstrumentComplete } = this.props
    const {
      idqId,
      completed,
      value,
      instrumentType,
      title,
      instanceId,
      statusTimestamp,
      statusEventType,
      row: fullRow,
    } = cell

    if (!idqId) return null

    const { instrumentsToComplete } = this.state
    const isMarkedComplete = value === 'Marked complete'
    const resolvedTitle = title || fullRow?.[0]?.value
    const showStatusTimelineIcon = this.canShowInstrumentStatusAction()

    if (!hasMarkInstrumentComplete && !showStatusTimelineIcon) return null

    const actionCellClassName = hasMarkInstrumentComplete ? 'complete-cell' : 'status-timeline-cell'

    return (
      <td className={actionCellClassName} key={`complete-${idqId}-${idx}`}>
        {showStatusTimelineIcon && (
          <Button
            link
            type='button'
            className='status-timeline-icon'
            icon='fas fa-history'
            title='View delivery status'
            aria-label='View delivery status'
            onClick={event => {
              event.stopPropagation()
              this.openStatusTimeline({
                idqId,
                instanceId: instanceId || idqId,
                instrumentType,
                title: resolvedTitle,
                statusTimestamp,
                statusEventType,
                completed,
              })
            }}
          />
        )}
        {hasMarkInstrumentComplete && (
          <span className='instrument-mark-complete-action'>
            {completed ? (
              <>{isMarkedComplete ? value : ''}</>
            ) : (
              <Checkbox
                label='Completed'
                checked={instrumentsToComplete[idqId]}
                onClick={event => {
                  event.stopPropagation()
                  this.toggleInstrumentToComplete(idqId)
                }}
              />
            )}
          </span>
        )}
      </td>
    )
  }

  renderCompletionCell = (row, idx) => {
    const { completed, value } = row
    return (
      <td className='completion-cell' key={`completion-date-${row}-${idx}`}>
        {completed ? (
          <>
            <i className='fas fa-check' /> <span>{value}</span>
          </>
        ) : (
          <i className='fas fa-times' />
        )}
      </td>
    )
  }

  renderStatusCell = (row, idx) => {
    const { value } = row
    return (
      <td className='completion-cell' key={`completion-date-${row}-${idx}`}>
        <span>{value}</span>
      </td>
    )
  }

  selectCycle = num => {
    return () => {
      this.setState({ currentCycle: num })
    }
  }

  getFilteredByCycle = () => {
    const { instrumentStatus } = this.props
    const { currentCycle } = this.state

    const inCycle = currentCycle !== NOT_APPLICABLE_PLACEHOLDER

    if (currentCycle === null) return instrumentStatus

    return instrumentStatus.filter(data => {
      return inCycle ? parseInt(data[1].value, 10) === currentCycle : data[1].value === currentCycle
    })
  }

  getCycleNums = () => {
    const { hasRewards, instrumentStatus } = this.props
    if (!hasRewards) return null
    const set = {}
    instrumentStatus.forEach(datum => {
      const hasCycleNum = datum[1].value !== 'Post-study' && datum[1].value !== ''
      if (hasCycleNum) set[datum[1].value] = null
    })

    return Object.keys(set)
  }

  renderCycleTabs = () => {
    const { hasRewards } = this.props
    const { currentCycle } = this.state
    const cycleNums = this.getCycleNums()
    if (!hasRewards) return null
    return (
      <div className='cycle-tabs'>
        <span onClick={this.selectCycle(null)} className={`clickable${currentCycle === null ? ' selected' : ''}`}>
          All Chapters
        </span>
        {cycleNums &&
          cycleNums.map(_num => {
            const inCycle = _num !== NOT_APPLICABLE_PLACEHOLDER
            const num = inCycle ? parseInt(_num, 10) : NOT_APPLICABLE_PLACEHOLDER
            return (
              <span
                key={`tab_cycle_${num}`}
                className={`clickable${currentCycle === num ? ' selected' : ''}`}
                onClick={this.selectCycle(inCycle ? parseInt(num, 10) : NOT_APPLICABLE_PLACEHOLDER)}>
                {inCycle ? `Chapter ${num}` : NOT_APPLICABLE_PLACEHOLDER}
              </span>
            )
          })}
      </div>
    )
  }

  expandedRow = row => {
    const {
      instrumentTimeline,
      fetchInstrumentTimeline,
      resetInstrumentTimeline,
      instrumentTimelineLoading,
      site,
      mainSite,
    } = this.props
    const { idqId } = row[row.length - 1]
    return (
      <StatusHistorySection
        instrumentTimeline={instrumentTimeline}
        instrumentId={idqId}
        fetchInstrumentTimeline={fetchInstrumentTimeline}
        instrumentTimelineLoading={instrumentTimelineLoading}
        onClose={() => {
          this.setState({
            isStatusRowExpanded: {},
          })
          resetInstrumentTimeline()
        }}
        noStatusMessage={STRINGS.noInstrumentStatus}
        site={site}
        mainSite={mainSite}
        withUtcOffset
      />
    )
  }

  getTableRenderFunctions = () => {
    const { hasMarkInstrumentComplete, hasAdvancedEventTimelines, isV2Architecture } = this.props
    const returnNull = () => null
    const usesActionsMenu = this.usesInstrumentActionsMenu()
    const showStatusColumns = this.showInstrumentStatusColumns()
    const showActionColumn = hasMarkInstrumentComplete || isV2Architecture
    return {
      action: usesActionsMenu
        ? this.renderInstrumentActionsCell
        : showActionColumn
          ? this.renderCompleteInstrumentCell
          : returnNull,
      completionDate: usesActionsMenu || showStatusColumns ? returnNull : this.renderCompletionCell,
      cycleNo: usesActionsMenu || showStatusColumns ? returnNull : row => <td>{row.value}</td>,
      status: usesActionsMenu || !showStatusColumns ? returnNull : this.renderStatusCell,
      name: ({ value }) => (
        <td key={`instrument-${value}`} className='instrument-name'>
          {value}
        </td>
      ),
      statusTimestamp:
        usesActionsMenu || !showStatusColumns
          ? returnNull
          : ({ value }) => <td key={`instrument-${value}`}>{value || NOT_APPLICABLE_PLACEHOLDER}</td>,
    }
  }

  renderSubmissionViewer = () => {
    const {
      studyID,
      ptpId,
      instrumentSubmission,
      instrumentSubmissionLoading,
      fetchInstrumentSubmission,
      resetInstrumentSubmission,
    } = this.props
    const { selectedSubmissionRow } = this.state

    return (
      <Suspense
        fallback={
          <div className='instrument-submission-viewer'>
            <Loader inContainer className='instrument-submission-loader' />
          </div>
        }>
        <InstrumentSubmissionViewer
          selectedRow={selectedSubmissionRow}
          submission={instrumentSubmission?.data}
          submissionError={instrumentSubmission?.error}
          submissionTraceId={instrumentSubmission?.traceId}
          instrumentSubmissionLoading={instrumentSubmissionLoading}
          fetchInstrumentSubmission={fetchInstrumentSubmission}
          resetInstrumentSubmission={resetInstrumentSubmission}
          studyID={studyID}
          ptpId={ptpId}
          onClose={this.closeSubmissionViewer}
          instrumentOptions={this.getViewableInstrumentOptions()}
          onSelectInstrument={this.handleSelectInstrument}
        />
      </Suspense>
    )
  }

  hasInteractiveRows = () => {
    if (this.usesInstrumentActionsMenu()) return false
    return this.canShowInstrumentStatusAction()
  }

  hasStatusRowExpand = () => this.canShowInstrumentStatusAction()

  render() {
    const { hasMarkInstrumentComplete, ptpInstrumentsLoading, isV2Architecture } = this.props
    const { selectedSubmissionRow, isStatusRowExpanded } = this.state
    const list = this.getFilteredByCycle()
    const hasInstruments = list.length > 0
    const hasViewableResponses = this.getViewableInstrumentOptions().length > 0
    const showStatusExpand = this.hasStatusRowExpand()
    const renderFunctions = this.getTableRenderFunctions()

    if (selectedSubmissionRow) {
      return (
        <div>
          <Container className='ptp-instruments instrument-submission-container'>
            {this.renderSubmissionViewer()}
          </Container>
        </div>
      )
    }

    return (
      <div>
        <Container
          className={`ptp-instruments${hasInstruments ? ' fixed-min-height' : ''}${this.usesInstrumentActionsMenu() ? ' instrument-actions-table' : ''}`}>
          <div className='title'>Instruments</div>
          {ptpInstrumentsLoading ? (
            <Loader inContainer />
          ) : (
            <>
              {this.renderCycleTabs()}
              {hasMarkInstrumentComplete && hasInstruments && this.renderMarkCompleteButton()}
              {hasInstruments ? (
                <SortableTable
                  sortingBy='cycleNo'
                  headerOptions={this.headerOptions()}
                  rowList={list}
                  renderFunctions={renderFunctions}
                  onRowClick={this.hasInteractiveRows() ? this.handleInstrumentRowClick : null}
                  rowExpandOptions={
                    showStatusExpand
                      ? {
                          isRowExpanded: row => {
                            const { idqId } = row[row.length - 1]
                            return isStatusRowExpanded[idqId]
                          },
                          expandedRowClassName: 'expanded',
                        }
                      : null
                  }
                  expandedRow={this.expandedRow}
                />
              ) : (
                <p className='instrument-empty-message'>
                  {isV2Architecture && !hasViewableResponses
                    ? STRINGS.noInstrumentResponsesYet
                    : showStatusExpand
                      ? STRINGS.noInstrumentStatus
                      : noInstrumentsString}
                </p>
              )}
            </>
          )}
        </Container>
      </div>
    )
  }
}

CompletedInstruments.propTypes = {
  fetchInstrumentSubmission: PropTypes.func,
  fetchInstrumentTimeline: PropTypes.func,
  hasAdvancedEventTimelines: PropTypes.bool,
  hideInstrumentStatus: PropTypes.bool,
  hasMarkInstrumentComplete: PropTypes.bool,
  hasRewards: PropTypes.bool,
  instrumentStatus: PropTypes.arrayOf(PropTypes.array),
  instrumentSubmission: PropTypes.shape({
    data: PropTypes.object,
    error: PropTypes.string,
    traceId: PropTypes.string,
  }),
  instrumentSubmissionLoading: PropTypes.bool,
  instrumentTimeline: PropTypes.array,
  instrumentTimelineLoading: PropTypes.bool,
  isV2Architecture: PropTypes.bool,
  markInstrumentComplete: PropTypes.func,
  params: PropTypes.shape({
    siteID: PropTypes.string,
    studyID: PropTypes.string,
  }),
  ptpId: PropTypes.number,
  ptpInstrumentsLoading: PropTypes.bool,
  ptpSiteID: PropTypes.number,
  participant: PropTypes.shape({
    id: PropTypes.number,
  }),
  resetInstrumentSubmission: PropTypes.func,
  resetInstrumentTimeline: PropTypes.func,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

CompletedInstruments.defaultProps = {
  hideInstrumentStatus: false,
  isV2Architecture: false,
}

export default CompletedInstruments
